import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ADD_PRESCRIPTION_LINE_MUTATION,
  CLINICAL_PRESCRIPTIONS_QUERY,
  CLINICAL_PRESCRIPTION_LINES_QUERY,
  COMPLETE_PRESCRIPTION_MUTATION,
  CREATE_PRESCRIPTION_MUTATION,
  DISCONTINUE_PRESCRIPTION_MUTATION,
  ISSUE_PRESCRIPTION_MUTATION,
  REMOVE_PRESCRIPTION_LINE_MUTATION,
  idempotencyContext,
} from './clinical.gql';
import { printPrescription, type RxPrintLine } from './prescription-print';

import type { ClinicBranding } from './useClinicIdentity';

import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { showToast } from '@/shared/ui/toast';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */

const ROUTES = ['oral', 'topical', 'subcutaneous', 'intramuscular', 'intravenous', 'ophthalmic', 'otic', 'other'] as const;
// Statuses where the prescription has been issued and has real content -> printable.
// Exported so the history modal gates its reprint button on the same source of truth.
export const RX_PRINTABLE_STATUSES = new Set(['active', 'completed', 'discontinued']);

/** Header context shared by every prescription card for the print document. */
export interface RxPrintBase {
  patient_name: string;
  species: string | null;
  vet_name: string;
  visit_folio: string;
}

/** Builds the full print context + opens the print window; toasts if the popup is blocked. */
export async function openPrescriptionPrint(
  branding: ClinicBranding,
  base: RxPrintBase,
  rx: { id: string; created_at: string },
  lines: RxPrintLine[],
  t: (k: string) => string,
): Promise<void> {
  const ok = await printPrescription(
    branding,
    {
      ...base,
      issued_date: new Date(rx.created_at).toLocaleDateString(),
      rx_reference: rx.id.slice(0, 8).toUpperCase(),
    },
    lines,
    t,
  );
  if (!ok) showToast({ tone: 'error', title: t('clinical.rx_print_blocked') });
}

function errText(e: unknown): string {
  if (e instanceof ApolloError) return e.graphQLErrors[0]?.message ?? e.message;
  if (e instanceof Error) return e.message;
  return 'error';
}

/** Whole-number days or undefined — the Int DTO rejects floats/NaN. */
function intOrUndef(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number.parseInt(v, 10);
  return Number.isNaN(n) ? undefined : n;
}

function PrescriptionCard({
  rx,
  canWrite,
  editable,
  appendable,
  branding,
  printBase,
  onError,
  onChanged,
}: {
  rx: any;
  canWrite: boolean;
  editable: boolean;
  appendable: boolean;
  branding: ClinicBranding;
  printBase: RxPrintBase;
  onError: (m: string) => void;
  onChanged: () => Promise<unknown>;
}): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(CLINICAL_PRESCRIPTION_LINES_QUERY, {
    variables: { prescription_id: rx.id },
    fetchPolicy: 'cache-and-network',
  });
  const lines: any[] = data?.clinicalPrescriptionLines ?? [];
  const [addLine] = useMutation(ADD_PRESCRIPTION_LINE_MUTATION);
  const [removeLine] = useMutation(REMOVE_PRESCRIPTION_LINE_MUTATION);
  const [issue] = useMutation(ISSUE_PRESCRIPTION_MUTATION);
  const [discontinue] = useMutation(DISCONTINUE_PRESCRIPTION_MUTATION);
  const [complete] = useMutation(COMPLETE_PRESCRIPTION_MUTATION);

  const draftEditable = rx.status === 'draft' && editable && canWrite;
  const [line, setLine] = useState({ drug_name: '', dose: '', dose_unit: '', route: 'oral', frequency: '', duration_days: '', instructions: '' });
  const [discReason, setDiscReason] = useState('');
  const [showDisc, setShowDisc] = useState(false);

  const run = async (fn: () => Promise<unknown>, toast?: string): Promise<void> => {
    try {
      await fn();
      await refetch();
      await onChanged();
      if (toast) showToast({ tone: 'success', title: toast });
    } catch (e) {
      onError(errText(e));
    }
  };

  const onAddLine = (): Promise<void> =>
    run(async () => {
      if (line.drug_name.trim() === '') return;
      await addLine({
        variables: {
          input: {
            prescription_id: rx.id,
            drug_name: line.drug_name.trim(),
            dose: line.dose.trim() || undefined,
            dose_unit: line.dose_unit.trim() || undefined,
            route: line.route,
            frequency: line.frequency.trim() || undefined,
            duration_days: intOrUndef(line.duration_days),
            instructions: line.instructions.trim() || undefined,
          },
        },
        context: idempotencyContext(),
      });
      setLine({ drug_name: '', dose: '', dose_unit: '', route: 'oral', frequency: '', duration_days: '', instructions: '' });
    }, t('clinical.toast_rx_line_added'));

  return (
    <div className="space-y-2 rounded-md border p-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">
          {t('clinical.prescription')} · <span className="text-muted-foreground">{t(`clinical.rx_status_${rx.status}`)}</span>
        </p>
        <div className="flex gap-1">
          {RX_PRINTABLE_STATUSES.has(rx.status) ? (
            <Button size="sm" variant="outline" onClick={() => void openPrescriptionPrint(branding, printBase, rx, lines, t)}>
              {t('clinical.rx_print')}
            </Button>
          ) : null}
          {rx.status === 'draft' && editable && canWrite ? (
            <Button size="sm" variant="outline" onClick={() => void run(() => issue({ variables: { prescription_id: rx.id }, context: idempotencyContext() }), t('clinical.toast_rx_issued'))}>
              {t('clinical.rx_issue')}
            </Button>
          ) : null}
          {rx.status === 'active' && appendable && canWrite ? (
            <>
              <Button size="sm" variant="outline" onClick={() => void run(() => complete({ variables: { prescription_id: rx.id }, context: idempotencyContext() }), t('clinical.toast_rx_completed'))}>
                {t('clinical.rx_complete')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowDisc((v) => !v)}>
                {t('clinical.rx_discontinue')}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {showDisc ? (
        <div className="flex gap-2">
          <Input placeholder={t('clinical.rx_discontinue_reason')} value={discReason} onChange={(e) => setDiscReason(e.target.value)} />
          <Button size="sm" disabled={discReason.trim().length < 3} onClick={() => void run(async () => {
            await discontinue({ variables: { input: { prescription_id: rx.id, reason: discReason.trim() } }, context: idempotencyContext() });
            setShowDisc(false); setDiscReason('');
          }, t('clinical.toast_rx_discontinued'))}>
            {t('common.save')}
          </Button>
        </div>
      ) : null}

      {lines.length === 0 ? (
        <p className="text-muted-foreground text-xs">{t('clinical.no_rx_lines')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {lines.map((l) => (
            <li key={l.id} className="flex items-center justify-between gap-2">
              <span>
                {l.is_controlled ? <span className="text-destructive">▲ </span> : null}
                <strong>{l.drug_name}</strong>
                {l.dose ? ` · ${l.dose}${l.dose_unit ? ' ' + l.dose_unit : ''}` : ''}
                {` · ${t(`clinical.route_${l.route}`)}`}
                {l.frequency ? ` · ${l.frequency}` : ''}
                {l.duration_days ? ` · ${l.duration_days}d` : ''}
              </span>
              {draftEditable ? (
                <Button size="sm" variant="ghost" onClick={() => void run(() => removeLine({ variables: { line_id: l.id }, context: idempotencyContext() }))}>
                  {t('clinical.delete')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {draftEditable ? (
        <div className="grid grid-cols-2 gap-2 border-t pt-2">
          <Input placeholder={t('clinical.rx_drug')} value={line.drug_name} onChange={(e) => setLine({ ...line, drug_name: e.target.value })} />
          <select className="border-input bg-background h-10 rounded-md border px-2 text-sm" value={line.route} onChange={(e) => setLine({ ...line, route: e.target.value })}>
            {ROUTES.map((r) => <option key={r} value={r}>{t(`clinical.route_${r}`)}</option>)}
          </select>
          <Input placeholder={t('clinical.rx_dose')} value={line.dose} onChange={(e) => setLine({ ...line, dose: e.target.value })} />
          <Input placeholder={t('clinical.rx_dose_unit')} value={line.dose_unit} onChange={(e) => setLine({ ...line, dose_unit: e.target.value })} />
          <Input placeholder={t('clinical.rx_frequency')} value={line.frequency} onChange={(e) => setLine({ ...line, frequency: e.target.value })} />
          <Input type="number" min="0" step="1" placeholder={t('clinical.rx_duration')} value={line.duration_days} onChange={(e) => setLine({ ...line, duration_days: e.target.value })} />
          <Input className="col-span-2" placeholder={t('clinical.rx_instructions')} value={line.instructions} onChange={(e) => setLine({ ...line, instructions: e.target.value })} />
          <div className="col-span-2 flex justify-end">
            <Button size="sm" onClick={() => void onAddLine()} disabled={line.drug_name.trim() === ''}>{t('clinical.add_rx_line')}</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function PrescriptionsSection({
  visitId,
  canWrite,
  editable,
  appendable,
  branding,
  printBase,
}: {
  visitId: string;
  canWrite: boolean;
  editable: boolean;
  appendable: boolean;
  branding: ClinicBranding;
  printBase: RxPrintBase;
}): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(CLINICAL_PRESCRIPTIONS_QUERY, {
    variables: { visit_id: visitId },
    fetchPolicy: 'cache-and-network',
  });
  const prescriptions: any[] = data?.clinicalPrescriptions ?? [];
  const [createRx] = useMutation(CREATE_PRESCRIPTION_MUTATION);
  const [error, setError] = useState<string | null>(null);

  const onCreate = async (): Promise<void> => {
    setError(null);
    try {
      await createRx({ variables: { input: { clinical_visit_id: visitId } }, context: idempotencyContext() });
      await refetch();
      showToast({ tone: 'success', title: t('clinical.toast_rx_created') });
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <div className="space-y-3">
      {error ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">{error}</div>
      ) : null}
      {prescriptions.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('clinical.no_prescriptions')}</p>
      ) : (
        <div className="space-y-2">
          {prescriptions.map((rx) => (
            <PrescriptionCard key={rx.id} rx={rx} canWrite={canWrite} editable={editable} appendable={appendable} branding={branding} printBase={printBase} onError={setError} onChanged={refetch} />
          ))}
        </div>
      )}
      {canWrite && editable ? (
        <Button size="sm" onClick={() => void onCreate()}>{t('clinical.new_prescription')}</Button>
      ) : null}
    </div>
  );
}
